import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_ui, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { LOCAL__ui_generation_grid2d_set_hovered_dimensions } from '@/state/ui/generation/LOCAL__ui_generation_grid2d_set_hovered_dimensions';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { CONSTANTS__generation_settings } from '@shared/constants/CONSTANTS__generation_settings';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__seed } from '@shared/dto/generation/graph_invoke/_fields/_primitives/FIELD_PRIMITIVE__seed';
import { get_digits_count } from '@shared/utils/utils_number';
import { forwardRef, useMemo, useState } from 'react';
import { G_HOOK__graph_field_hashed_for_hover } from '../../_base/_hooks/G_HOOK__graph_field_hashed_for_hover';
import { G_BUTTON__seed_delete } from './G_BUTTON__seed_delete';

/*
 * ALIASES
 */

/*
 * COMPONENT
 */

interface PROPS {
  field: FIELD_PRIMITIVE__seed;
  value: number;
  colorize_bg: boolean;
}
export const G_LIST_ITEM__seed = forwardRef<HTMLDivElement, PROPS>(({ field, value, colorize_bg }, ref) => {
  const dispatch = use_dispatch();
  /*
   * STORE
   */

  const { user_settings } = use_slice_user(['user_settings']);

  const { generation_current } = use_slice_generation(['generation_current']);

  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  const { g_general__ui_disabled, g_hovered_grid2d_row, g_hovered_grid2d_column } = use_slice_ui([
    'g_general__ui_disabled',
    'g_hovered_grid2d_row',
    'g_hovered_grid2d_column',
  ]);

  const is_used_in_generation = useMemo(() => get_is_used_in_generation(), [field, generation_current]);

  /*
   * STATE
   */

  const component_id = `${ENUM__component_type.PAGE_GENERATION_SEED_BAR}_${field.get_value()}_${generation_current._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_GENERATION);

  const [is_mouse_over, set_is_mouse_over] = useState(false);
  const field_hashed = G_HOOK__graph_field_hashed_for_hover(field);

  /*
   * EVENTS
   */
  async function onClick() {
    if (g_general__ui_disabled) return;
    if (is_used_in_generation) return;
    if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return;

    await UTILS__graph_data.update_field(generation_current.tool.graph, field);
  }

  function onMouseEnter() {
    set_is_mouse_over(true);

    let column = field_hashed.meta.grid2d__is_column ? field_hashed : FIELD__base.CREATE_STUB();
    let row = field_hashed.meta.grid2d__is_row ? field_hashed : FIELD__base.CREATE_STUB();

    dispatch(LOCAL__ui_generation_grid2d_set_hovered_dimensions({ column, row, g_hovered_grid2d_dimension_from_grid: false }));
  }

  function onMouseLeave() {
    set_is_mouse_over(false);

    dispatch(
      LOCAL__ui_generation_grid2d_set_hovered_dimensions({
        column: FIELD__base.CREATE_STUB(),
        row: FIELD__base.CREATE_STUB(),
        g_hovered_grid2d_dimension_from_grid: false,
      }),
    );
  }

  /*
   * METHODS
   */

  function get_is_used_in_generation() {
    if (generation_current.is_stub()) return false;

    let field_from_graph = generation_current.tool.graph.field__get_by_id(field.meta.field_id);
    assert_instanceof(field_from_graph, FIELD_PRIMITIVE__seed);

    return field_from_graph.get_value() == field.get_value();
  }

  function get_class_name() {
    let out = '';

    if (field.meta.grid2d__is_column && g_hovered_grid2d_column.meta.field_id == field.meta.field_id) {
      assert_instanceof(g_hovered_grid2d_column, FIELD_PRIMITIVE__seed);

      out = g_hovered_grid2d_column.is_equal(field_hashed) ? 'g_sidebar_list_item_hovered' : '';
    } else if (field.meta.grid2d__is_row && g_hovered_grid2d_row.meta.field_id == field.meta.field_id) {
      assert_instanceof(g_hovered_grid2d_row, FIELD_PRIMITIVE__seed);

      out = g_hovered_grid2d_row.is_equal(field_hashed) ? 'g_sidebar_list_item_hovered' : '';
    } else if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) {
      out = 'bg-component';
    }

    return out;
  }

  /*
   * RENDERS
   */

  function render_button_delete() {
    if (!is_mouse_over) return <></>;
    if (user_settings.generation_seeds_hide_input) return <></>;

    return <G_BUTTON__seed_delete {...{ field }} />;
  }

  function render_zeros() {
    let digits_count_max = get_digits_count(CONSTANTS__generation_settings.SEED_RANGE.max);
    let digits_count = get_digits_count(field.get_value());
    let zeros_count = digits_count_max - digits_count;

    return '0'.repeat(zeros_count);
  }

  /*
   * HTML
   */

  //all list items have by default a transparent border so that the text doesnt move when hovering
  return (
    <div
      ref={ref}
      className={`g_sidebar_list_item !w-1/2 max-h-7
        px-1
        ${colorize_bg && 'bg-component-item-odd'}
        ${is_used_in_generation && 'g_sidebar_list_item_hovered !cursor-default'}
        ${get_class_name()}
        ${g_general__ui_disabled ? '!cursor-default' : ''}
        `}
      {...{ onClick, onMouseEnter, onMouseLeave }}
    >
      <NotifierBorder {...{ notifier }} />

      <span className="opacity-10"> {render_zeros()}</span>
      <span className={``}>{value}</span>

      <span className="flex flex-vertical-center ml-auto ">{render_button_delete()}</span>
    </div>
  );
});
